import { memo, useMemo } from "react";
import { Position } from "../../proto/generated/position";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { selectOrdersHistory } from "../../selectors/orders";
import { getAnalyzedGroupedPosition } from "../../util/positions";
import { useStyles } from "./styles";
import AnalyzePositionsTableRow from "./analyzePositionsTableRow";
import { selectBasketId, selectBatchId, selectIceberg, selectTradeOrders } from "../../selectors/strategyBuilder";
import { icebergBasketStrategyDisplayQuantity } from "../../util/icebergOrders";
import { toLocaleStringIn } from "../../util/general";
import LtpCell from "./ltpCell";
import { AnalyzeGroupedPosition } from "../../reducer/positions.reducer";
import AnalyzePositionsSummaryProfitCell from "./analyzePositionsSummaryProfitCell";

export type AnalyzePositionsTableProps = {
    positions: Position[]
    handleProfitChange?: (realisedProfit: number, unrealisedProfit: number) => void
}

const AnalyzePositionsTable: React.FC<AnalyzePositionsTableProps> = ({positions, handleProfitChange}) => {

    const { classes, cx } = useStyles();

    const basketId = useSelector(selectBasketId);
    const batchId = useSelector(selectBatchId);
    const iceberg = useSelector(selectIceberg);
    const placedOrders = useSelector(selectOrdersHistory);
    const tradeOrders = useSelector(selectTradeOrders);
    const analyzeGroupedPosition = useMemo((): AnalyzeGroupedPosition | null => {
        return getAnalyzedGroupedPosition(positions as unknown as Position[], basketId, placedOrders, tradeOrders);
    }, [basketId, positions, placedOrders, tradeOrders])

    const tableHeader = useMemo(() => (
        <TableRow >
            <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                <Typography variant="caption" color="textSecondary">SYMBOL</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                <Typography variant="caption" color="textSecondary">QTY</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.averageCell)}>
                <Typography variant="caption" color="textSecondary">AVG</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                <Typography variant="caption" color="textSecondary">LTP</Typography>
            </TableCell>
            <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ position: 'relative' }}>
                <Typography variant="caption" color="textSecondary">PRFT</Typography>
            </TableCell>
        </TableRow>), [])

    const icebergDisplayQty = useMemo(() => {
        if (!iceberg.enabled) return null;
        return icebergBasketStrategyDisplayQuantity(placedOrders, batchId, basketId, tradeOrders);
    }, [iceberg.enabled, placedOrders, batchId, basketId, tradeOrders]);

    const quantityDisplay = useMemo(() => {
        const baseQty = analyzeGroupedPosition?.newQuantity ?? analyzeGroupedPosition?.quantity ?? 0;
        const currentQty = icebergDisplayQty ?? baseQty;

        if (baseQty === 0 && analyzeGroupedPosition?.children?.length) {
            const allPositions = analyzeGroupedPosition.children;
            const originalQuantities = allPositions.map(p => Math.abs(p.quantity)).sort();
            const originalQty = allPositions.length > 1
                ? (originalQuantities.length > 0 ? originalQuantities.reduce((a, b) => {
                    const gcd = (x: number, y: number): number => y === 0 ? x : gcd(y, x % y);
                    return gcd(a, b);
                  }) : 0)
                : (originalQuantities[0] || 0);
            const wasPositive = allPositions[0]?.quantity > 0;

            return <>
                <span className={cx(
                    classes.oldQtyDisplay,
                    wasPositive ? classes.buyQuantity : classes.sellQuantity
                )}>{wasPositive ? originalQty : -originalQty}</span>
                <span>{0}</span>
            </>;
        }

        return <span className={cx(
            currentQty > 0 ? classes.buyQuantity : undefined,
            currentQty < 0 ? classes.sellQuantity : undefined
        )}>{currentQty}</span>
    }, [analyzeGroupedPosition, classes, icebergDisplayQty])

    return <Table className={classes.table}>
            <TableHead>
                {tableHeader}
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                        {analyzeGroupedPosition?.groupName}
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                        <Typography variant="caption">
                            {quantityDisplay}
                        </Typography>
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.averageCell)}>
                        {analyzeGroupedPosition.avgPrice ? toLocaleStringIn(analyzeGroupedPosition?.avgPrice/100) : '-'}
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                        {analyzeGroupedPosition ? <LtpCell position={analyzeGroupedPosition} /> : '-'}
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ verticalAlign: 'middle', padding: '5px', position: 'relative' }}>
                        {analyzeGroupedPosition ? (
                            <AnalyzePositionsSummaryProfitCell
                                analyzeGroupedPosition={analyzeGroupedPosition}
                                positions={positions}
                                handleProfitChange={handleProfitChange}
                            />
                        ) : '-'}
                    </TableCell>
                </TableRow>
                {
                    analyzeGroupedPosition ? analyzeGroupedPosition.children.map((position, i) => (
                        <AnalyzePositionsTableRow key={`${position.groupId}-${getSecurityKey(position.security.exchange, position.security.code)}-${i}`} position={position} />
                    )) : null
                }
            </TableBody>
    </Table>;
}


export default memo(AnalyzePositionsTable)